/**
 * Calculate the fixed height for a responsibility card based on the number of prompts
 * Accounts for text wrapping in prompt titles which can cause variable heights
 * @param {number} promptCount - The number of prompts in the responsibility
 * @returns {number} The calculated height in pixels
 */
export const calculateResponsibilityCardHeight = (promptCount) => {
  const count = Math.max(promptCount || 0, 0);
  
  // Height calculation breakdown:
  // - Top tip: 8px (fixed)
  // - Content padding top: 20px
  // - Title: ~26px (20px font size * 1.3 line-height, can wrap to 2 lines = ~52px)
  // - Gap between title and prompts: 16px
  // - Prompts: variable per item based on count
  //   * Each prompt item has: 8px padding top + 11px padding bottom = 19px
  //   * Prompt title: 14px font-size, default line-height ~1.2-1.4 = ~17-20px per line
  //   * Titles can wrap to 2-3 lines, so estimate ~30px average height per prompt title
  //   * For minimum (5 prompts): 19px padding + ~30px title = ~49px per prompt
  //   * For counts > 5: Use slightly reduced ~47px per prompt to reduce height growth
  // - Dividers: (count - 1) * 4px (1px height + 3px margin-top, only if count > 0)
  // - Content padding bottom: 16px (reduced from 28px)
  // - Additional buffer for comfortable spacing and wrapping: 20px
  // 
  // For count = 0: 8 + 20 + 26 + 16 + 16 + 20 = 106px (empty state)
  // For count <= 5: 8 + 20 + 26 + 16 + (count * 49) + ((count - 1) * 4) + 16 + 20
  //                = 106 + 49*count + 4*count - 4 = 102 + 53*count
  // For count > 5: Base height for 5 prompts + additional prompts at reduced rate
  //                = (102 + (5 * 53)) + ((count - 5) * 51)
  //                = 367 + ((count - 5) * 51)
  
  if (count === 0) {
    return 106; // Empty state height
  }
  
  if (count <= 5) {
    // Use full calculation for minimum height (5 prompts)
    return 102 + (count * 53);
  }
  
  // For counts > 5, use base height for 5 prompts + reduced rate for additional prompts
  const baseHeightFor5 = 102 + (5 * 53); // 367px
  return baseHeightFor5 + ((count - 5) * 35);
};

/**
 * Calculate the maximum height needed across all responsibilities
 * Ensures a minimum height for at least 5 prompts
 * @param {Array<Object>} responsibilities - Array of responsibility objects
 * @param {Object} frameworks - Object mapping responsibilityId to frameworks array
 * @returns {number} The maximum height in pixels
 */
export const calculateMaxResponsibilityCardHeight = (responsibilities, frameworks) => {
  if (!responsibilities || responsibilities.length === 0) {
    // Return minimum height for 5 prompts
    return calculateResponsibilityCardHeight(5);
  }

  // Find the maximum number of prompts across all responsibilities
  const maxPrompts = Math.max(
    ...responsibilities.map(resp => {
      const promptList = frameworks[resp.responsibilityId] || [];
      return promptList.length || 0;
    })
  );

  // Ensure minimum height for at least 5 prompts
  const effectivePromptCount = Math.max(maxPrompts, 5);

  return calculateResponsibilityCardHeight(effectivePromptCount);
};
